import { isReference } from '@apollo/client';
import type {
  FetchResult,
  InternalRefetchQueriesInclude,
  Reference,
  StoreObject,
} from '@apollo/client';

import { isDevelopment } from '@constants/env';

import { useResponseHandler } from '@hooks/useResponseHandler';
import { useSession } from '@hooks/useSession';

import { ConsentRecord } from '@models/consent';
import type { GetFhirRecordParams } from '@models/fhir-resource';

import { parseArgs, type CachedRecordList } from '@utils/apollo';

import type {
  PostConsentRecordMutationVariables,
  PostConsentRecordMutation,
  UpdateConsentRecordMutationVariables,
  UpdateConsentRecordMutation,
} from 'generated/consent.generated';
import {
  useGetConsentQuery,
  useListConsentsQuery,
  usePostConsentRecordMutation,
  useUpdateConsentRecordMutation,
} from 'generated/consent.generated';

export const useGetConsents = () => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile, active_user_id, logged_in_user_id },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useListConsentsQuery({
    variables: {
      input: {
        patient: patient_id,
        count: 1500,
        page: 1,
      },
    },
    skip: !patient_id,
  });
  const { data, error } = response;

  // A caregiver granted access to a patient may not also be granted permission
  // to list that patient's consents. The backend still allows record access, so
  // a consent error on someone else's profile must not throw to the error
  // boundary and softlock the caregiver. Only surface the error (and prompt to
  // consent) when the active profile is the logged-in user's own.
  const isViewingOtherProfile = Boolean(
    active_user_id && logged_in_user_id && active_user_id !== logged_in_user_id
  );
  const consentUnavailable = Boolean(error) && isViewingOtherProfile;
  if (!consentUnavailable) {
    handleError(error);
  }

  const records = (data?.listConsents.data ?? []).map(
    (r) => new ConsentRecord(r)
  );
  return { ...response, data: records, consentUnavailable };
};

export const useGetConsent = (params: GetFhirRecordParams) => {
  const { handleError } = useResponseHandler();
  const {
    state: { activeProfile },
  } = useSession();
  const patient_id = activeProfile?.patient_id ?? '';
  const response = useGetConsentQuery({
    variables: { input: { record_id: params.record_id, patient_id } },
    skip: !patient_id || !params.record_id || Boolean(params.skip),
  });
  const { data, error } = response;
  handleError(error);
  const record = data?.getConsent;
  return { ...response, data: record ? new ConsentRecord(record) : null };
};

export function useCreateConsent(): [
  (
    params: PostConsentRecordMutationVariables & { display_success?: boolean }
  ) => Promise<FetchResult<PostConsentRecordMutation>>,
  boolean,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'upsertConsent',
  });
  const [mutate, { loading: baseLoading }] = usePostConsentRecordMutation();
  const loading = baseLoading;

  async function createConsent(
    params: PostConsentRecordMutationVariables & { display_success?: boolean }
  ) {
    const refetchQueries: InternalRefetchQueriesInclude = [];
    const display_success = params.display_success ?? true;
    const consent = params.input;

    const response = await mutate({
      variables: { ...params },
      refetchQueries,
      update: (cache, { data: response }) => {
        if (!response?.postConsentRecord) {
          return;
        }
        const storeObject =
          response.postConsentRecord as unknown as StoreObject;
        try {
          cache.modify<{ listConsents: CachedRecordList }>({
            fields: {
              listConsents: (existing, { storeFieldName, toReference }) => {
                if (isReference(existing)) {
                  return existing;
                }
                const parsed = parseArgs(storeFieldName);
                if (parsed?.patient === consent.patient?.reference) {
                  return {
                    ...existing,
                    data: [...existing.data, toReference(storeObject)],
                  };
                }
                return existing;
              },
            },
          });
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    const { data, errors } = response;
    if (errors) {
      handleError(errors);
    }
    if (data?.postConsentRecord && display_success && !errors) {
      handleSuccess();
    }
    return response;
  }
  return [createConsent, loading];
}

export function useUpdateConsent(): [
  (
    params: UpdateConsentRecordMutationVariables
  ) => Promise<FetchResult<UpdateConsentRecordMutation>>,
  boolean,
] {
  const { handleError, handleSuccess } = useResponseHandler({
    handlerType: 'snackbar',
    snackbarMsgId: 'upsertConsent',
  });
  const [mutate, { loading: baseLoading }] = useUpdateConsentRecordMutation();
  const loading = baseLoading;

  async function updateConsent(params: UpdateConsentRecordMutationVariables) {
    const response = await mutate({
      variables: { ...params },
      update: (cache, { data: response }) => {
        if (!response?.updateConsentRecord) {
          return;
        }
        const storeObject =
          response.updateConsentRecord as unknown as StoreObject;
        const { consent, consent_id } = params.input;
        try {
          cache.modify<{ listConsents: Reference[] }>({
            fields: {
              listConsents: (existing, { storeFieldName, toReference }) => {
                const parsed = parseArgs(storeFieldName);
                if (parsed?.patient === consent.patient?.reference) {
                  const index = existing.findIndex(({ __ref }) =>
                    __ref.endsWith(consent_id)
                  );
                  const newRef = toReference(storeObject);
                  if (index > -1 && newRef) {
                    return [
                      ...existing.slice(0, index),
                      newRef,
                      ...existing.slice(index + 1),
                    ];
                  }
                }
                return existing;
              },
            },
          });
        } catch (e) {
          if (isDevelopment) {
            // eslint-disable-next-line no-console
            console.error(e);
          }
        }
      },
    });
    const { data, errors } = response;
    if (errors) {
      handleError(errors);
    }
    if (data?.updateConsentRecord && !errors) {
      handleSuccess();
    }
    return response;
  }
  return [updateConsent, loading];
}
